import { motion } from 'framer-motion';
import { Brain, MessageSquare, AlertTriangle, Workflow, Wrench, BarChart3, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const featureSections = [
  {
    icon: Brain,
    badge: 'V1',
    color: '#00d4ff',
    title: 'Auto-Semantic Discovery',
    desc: 'DQP automatically profiles every connected schema, infers relationships across databases, and maintains a living data glossary that evolves with your data.',
    details: [
      'Schema profiling with column-level statistics',
      'Cross-database relationship inference using AI',
      'Auto-generated business glossary with plain-language definitions',
      'Lineage tracking from source to consumption',
      'Continuous re-profiling on schema changes',
    ],
  },
  {
    icon: MessageSquare,
    badge: 'V1',
    color: '#3b82f6',
    title: 'Conversational Query',
    desc: 'Ask your data questions in natural language. DQP translates to optimized SQL, executes across federated sources, and returns explainable answers with full session memory.',
    details: [
      'Natural language to optimized SQL translation',
      'Multi-turn conversation with context memory',
      'Query explanation with step-by-step reasoning',
      'Cross-database federated query execution',
      'Suggested follow-up questions',
    ],
  },
  {
    icon: AlertTriangle,
    badge: 'V1',
    color: '#f59e0b',
    title: 'Insight Review & Registry',
    desc: 'Every AI-generated insight is flagged for human review. Anomaly detection surfaces what matters. A governance registry ensures nothing reaches production ungoverned.',
    details: [
      'AI-detected anomalies and data quality insights',
      'Human-in-the-loop approval workflow',
      'Insight confidence scoring and prioritization',
      'Audit trail for every approved insight',
      'Customizable governance rules and policies',
    ],
  },
  {
    icon: Workflow,
    badge: 'V2',
    color: '#8b5cf6',
    title: 'Visual Workflow Canvas',
    desc: 'Build complex data pipelines with a drag-and-drop canvas. Orchestrate transformations, schedule runs, and monitor execution in real-time.',
    details: [
      'Drag-and-drop pipeline builder',
      'Real-time execution monitoring',
      'Conditional branching and error handling',
      'Schedule-based and event-driven triggers',
      'Version history and rollback',
    ],
  },
  {
    icon: Wrench,
    badge: 'V2',
    color: '#ec4899',
    title: 'Data Wrangling',
    desc: 'Clean, transform, and reshape data without writing code. Visual transformations with preview, undo, and reproducible recipes.',
    details: [
      'Visual data transformation builder',
      'Live preview of transformation results',
      'Reusable transformation recipes',
      'Automated data quality checks',
      'Export to any connected destination',
    ],
  },
  {
    icon: BarChart3,
    badge: 'V3',
    color: '#10b981',
    title: 'Native Visualization',
    desc: 'Build dashboards and reports directly from your semantic graph. No more exporting to separate BI tools.',
    details: [
      'Drag-and-drop dashboard builder',
      'Semantic-aware chart suggestions',
      'Real-time data refresh',
      'Embedded sharing and white-label options',
      'Template library for common use cases',
    ],
  },
];

export default function Features() {
  return (
    <div className="bg-[#0a0a1a] min-h-screen">
      {/* Hero */}
      <section data-testid="features-hero" className="relative py-24 md:py-32">
        <div className="glow-spot w-[300px] h-[300px] bg-[#00d4ff] top-[-50px] left-[20%] opacity-10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Features</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Built for the way<br /><span className="gradient-text">data teams work</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400 max-w-2xl mx-auto">
              Every feature designed to reduce complexity, accelerate insight, and maintain governance at enterprise scale.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Feature Sections */}
      <section data-testid="features-list" className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16">
          {featureSections.map((feature, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              data-testid={`feature-section-${i}`}
              className={`glass-card p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center ${i % 2 === 1 ? 'md:direction-rtl' : ''}`}
            >
              <div className={i % 2 === 1 ? 'md:order-2' : ''}>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${feature.color}15` }}>
                    <feature.icon className="w-5 h-5" style={{ color: feature.color }} />
                  </div>
                  <Badge variant="outline" className={`border-white/10 text-xs ${feature.badge === 'V1' ? 'text-emerald-400' : feature.badge === 'V2' ? 'text-amber-400' : 'text-purple-400'}`}>
                    {feature.badge === 'V1' ? 'Available Now' : `Coming in ${feature.badge}`}
                  </Badge>
                </motion.div>
                <motion.h3 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                  {feature.title}
                </motion.h3>
                <motion.p variants={fadeUp} className="text-slate-400 leading-relaxed mb-6">
                  {feature.desc}
                </motion.p>
                <motion.ul variants={fadeUp} className="space-y-3">
                  {feature.details.map((d, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-slate-400">
                      <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: feature.color }} />
                      {d}
                    </li>
                  ))}
                </motion.ul>
              </div>
              <motion.div variants={fadeUp} className={`relative ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                <div className="aspect-video rounded-xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${feature.color}10, ${feature.color}05)` }}>
                  <div className="w-full h-full flex items-center justify-center">
                    <feature.icon className="w-16 h-16 opacity-20" style={{ color: feature.color }} />
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 text-center bg-[#111122]/50">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto px-6">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit' }}>
            Ready to explore?
          </motion.h2>
          <motion.div variants={fadeUp} className="flex justify-center gap-4">
            <Link to="/signup">
              <Button data-testid="features-cta-btn" className="bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full px-8 py-3 h-12 hover:opacity-90 shadow-[0_0_20px_rgba(0,212,255,0.3)]">
                Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
